import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import customFetchBase from '../expiredToken/expiredToken';
import {
  ISportSettingsResponse,
  ICreateEditSportLocationSettings,
  ISportUserPreferencesResponse,
  INextGamesResponse,
  ISportLeaguesResponse,
  ISportTeamsResponse,
  ISportTemplateResponse,
  PostNewSportPreference,
  ISportTroubleshootingItem
} from '../../types/sports/sportsInterfaces';
import { TeamByLeague } from '../../types/teamByLeague';

const customFetch = customFetchBase(config.baseUrlDealers);

export const apiSport = createApi({
  reducerPath: 'apiLocations',
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getSportLocationSettings: builder.mutation<ISportSettingsResponse, number>({
      query: (locationId) => ({
        url: `/api/SportLocationSettings/${locationId}`,
        method: 'GET'
      })
    }),
    createSportLocationSettings: builder.mutation<
      ISportSettingsResponse,
      ICreateEditSportLocationSettings[]
    >({
      query: (payload) => ({
        url: `/api/SportLocationSettings`,
        method: 'POST',
        body: payload
      })
    }),
    editSportLocationSettings: builder.mutation<
      ISportSettingsResponse,
      ICreateEditSportLocationSettings[]
    >({
      query: (payload) => ({
        url: `/api/SportLocationSettings`,
        method: 'PUT',
        body: payload
      })
    }),
    getSportUserPreferences: builder.mutation<
      ISportUserPreferencesResponse,
      number
    >({
      query: (locationId) => ({
        url: `/api/SportUserPreferences/Location/${locationId}`,
        method: 'GET'
      })
    }),
    setSportUserPreferences: builder.mutation<
      ISportUserPreferencesResponse,
      PostNewSportPreference
    >({
      query: (data) => ({
        url: `/api/SportUserPreferences`,
        method: 'POST',
        body: data
      })
    }),
    getNextGamesInfoByLocation: builder.mutation<INextGamesResponse, number>({
      query: (locationId) => ({
        url: `/api/SportSchedules/NextGames/${locationId}`,
        method: 'GET'
      })
    }),
    getTeamsByLeague: builder.mutation<{ data: TeamByLeague[] }, string>({
      query: (league) => ({
        url: `/api/Sports/Teams?league=${league}`,
        method: 'GET'
      })
    }),
    setNewExecutionPriority: builder.mutation<
      any,
      { locationId: number; order: { id: number; executionOrder: number }[] }
    >({
      query: (payload) => ({
        url: `/api/SportUserPreferences/Reorder`,
        method: 'PUT',
        body: payload
      })
    }),
    deleteSportUserPreferences: builder.mutation<any, number>({
      query: (preferenceId) => ({
        url: `/api/SportUserPreferences/${preferenceId}`,
        method: 'DELETE'
      })
    }),
    getSportLeagues: builder.mutation<ISportLeaguesResponse, void>({
      query: () => ({
        url: `/api/SportLeagues`,
        method: 'GET'
      })
    }),
    getSportTeams: builder.mutation<ISportTeamsResponse, string>({
      query: (league) => ({
        url: `/api/Sports/Teams?league=${league}`,
        method: 'GET'
      })
    }),
    getSportTemplateByLeagueAndLocation: builder.mutation<
      ISportTemplateResponse,
      { league: string; locationId: number }
    >({
      query: (payload) => ({
        url: `/api/SportUserPreferences/Template?league=${payload.league}&locationId=${payload.locationId}`,
        method: 'GET'
      })
    }),
    getSportTroubleshooting: builder.mutation<
      ISportTroubleshootingItem[],
      {
        locationId: number;
        dateExecuteAtStart: string;
        dateExecuteAtEnd: string;
      }
    >({
      query: (payload) => ({
        url: `/api/SportComputedGames`,
        method: 'GET',
        params: payload
      })
    })
  })
});

export const {
  useGetSportLocationSettingsMutation,
  useCreateSportLocationSettingsMutation,
  useEditSportLocationSettingsMutation,
  useGetSportUserPreferencesMutation,
  useGetNextGamesInfoByLocationMutation,
  useSetNewExecutionPriorityMutation,
  useGetTeamsByLeagueMutation,
  useSetSportUserPreferencesMutation,
  useDeleteSportUserPreferencesMutation,
  useGetSportLeaguesMutation,
  useGetSportTeamsMutation,
  useGetSportTemplateByLeagueAndLocationMutation,
  useGetSportTroubleshootingMutation
} = apiSport;
